import dayjs from 'dayjs';

export type DateLike =
  | Date
  | {toDate?: () => Date; _seconds?: number; seconds?: number; nanoseconds?: number}
  | {_methodName?: string}
  | null
  | undefined;

function toDate(value: DateLike): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && '_methodName' in value) return null;
  if (typeof (value as {toDate?: () => Date}).toDate === 'function') {
    return (value as {toDate: () => Date}).toDate();
  }
  const sec = (value as {_seconds?: number})._seconds ?? (value as {seconds?: number}).seconds;
  if (typeof sec === 'number') return new Date(sec * 1000);
  return null;
}

/**
 * Format a Date to a localized date string
 * @param date - Date object, Firestore timestamp, or null/undefined
 * @returns Formatted date string or 'N/A' if date is null/undefined
 */
export default function formatDate(date: DateLike): string {
  const d = toDate(date);
  if (!d) return 'N/A';
  return dayjs(d).format('MMM D, YYYY h:mm A');
}

/**
 * Format a duration between two date-like values.
 * @param start - Start time (Firestore timestamp or Date)
 * @param end - End time (optional; defaults to "now" when omitted)
 */
export function formatDuration(start: DateLike, end?: DateLike): string {
  const startDate = dayjs(toDate(start) ?? 0);
  const endDate = end != null ? dayjs(toDate(end) ?? 0) : dayjs();
  const durationSeconds = endDate.diff(startDate, 'second');

  if (durationSeconds < 0) return '0s';
  if (durationSeconds < 60) return `${durationSeconds}s`;
  if (durationSeconds < 3600) {
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }
  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}
